import { useEffect, useState } from "react";
import { Panel } from "./Surface";
import { Button } from "./ui/Button";
import { Icon } from "../app/Icon";
import { listSessions, signOutSession, signOutOtherSessions, deviceLabel, type AccountSession } from "../lib/sessions";

function when(at: string): string {
  return new Date(at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}

export function ActiveSessions() {
  const [sessions, setSessions] = useState<AccountSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  async function load() {
    setSessions((await listSessions()).sessions);
  }
  useEffect(() => {
    load().catch(() => undefined).finally(() => setLoading(false));
  }, []);

  async function revoke(id: string) {
    setBusy(id);
    try {
      await signOutSession(id);
      await load();
    } finally {
      setBusy(null);
    }
  }
  async function revokeOthers() {
    setBusy("others");
    try {
      await signOutOtherSessions();
      await load();
    } finally {
      setBusy(null);
    }
  }

  const others = sessions.filter((s) => !s.current).length;

  return (
    <Panel title="Active sessions">
      <p className="text-sm text-muted">These are the devices currently signed in to your account.</p>
      {loading ? (
        <p className="font-mono text-sm text-muted">Loading…</p>
      ) : sessions.length === 0 ? (
        <p className="font-mono text-sm text-muted">No active sessions.</p>
      ) : (
        <ul className="grid">
          {sessions.map((s) => (
            <li key={s.id} className="flex items-center gap-3 border-t border-line py-3 first:border-t-0 first:pt-0">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-surface-2 text-muted"><Icon name="security" className="h-4 w-4" /></span>
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 text-sm font-bold">
                  {deviceLabel(s.user_agent)}
                  {s.current && <span className="rounded-md border border-verified/40 px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest text-verified">Current</span>}
                </p>
                <p className="truncate font-mono text-xs text-muted">{s.ip ?? "unknown IP"} · {when(s.updated_at)}</p>
              </div>
              {s.current ? (
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted">This device</span>
              ) : (
                <Button variant="ghost" disabled={busy === s.id} onClick={() => revoke(s.id)}>Sign out</Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {others > 0 && (
        <div className="flex items-center justify-between gap-3 border-t border-line pt-3">
          <p className="font-mono text-xs text-muted">Don't recognize a session? Secure your account by signing out the others.</p>
          <Button variant="ghost" disabled={busy === "others"} onClick={revokeOthers}>Sign out all other sessions</Button>
        </div>
      )}
    </Panel>
  );
}
